// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pluralize } from "@sc/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;

    return {
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        isEstimatedItemCount: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
        // XNG-Changes
        isLoading: state.pages.productList.isLoading,
    };
};

// XNG-Changes: add HasCategoryContext
type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasCategoryContext;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    // XNG-Changes
    titleWrapper?: InjectableCss;
    textWrapper?: TypographyPresentationProps;
    resultsForText?: TypographyPresentationProps;
    categoryContentWrapper?: InjectableCss;
}

export const productCountStyles: ProductListProductCountStyles = {
    wrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({ totalItemCount, query, category, isLoading, isEstimatedItemCount }) => {
    // XNG-Changes: change from !totalItemCount to isLoading
    if (isLoading) {
        return null;
    }

    // XNG-Changes: remove OOTB code
    // const itemsText =
    //     (totalItemCount !== 1 ? " items" : " item") +
    //     (isEstimatedItemCount ? " (estimated)" : "") +
    //     (query ? " for " : "");

    // XNG-Changes: structure and add CategoryDetailsContent
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                {query && (
                    <StyledWrapper {...styles.textWrapper}>
                        <Typography {...styles.resultsForText}>{translate("Results for")}</Typography>
                        <Typography {...styles.queryText} data-test-selector="productListQueryText">
                            {query}
                        </Typography>
                    </StyledWrapper>
                )}
                {!query && category && (
                    <Typography
                        {...styles.queryText}
                        data-test-selector={`categoryName_${category.shortDescription ?? category.name}`}
                    >
                        {category.shortDescription ?? category.name}
                    </Typography>
                )}
                <Typography {...styles.countText} data-test-selector="productListCountText">
                    {/* XNG-Changes: use it instead of query */}
                    {pluralize(totalItemCount || 0, "Product", { showNumber: true })}
                    {isEstimatedItemCount ? " (estimated)" : ""}
                </Typography>
            </StyledWrapper>
            {!query && category && (
                <StyledWrapper {...styles.categoryContentWrapper}>
                    {createWidgetElement("Category/CategoryDetailsContent", {
                        fields: {},
                    })}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(ProductListProductCount)),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
